import React, { FC } from 'react';
import { map, values } from 'lodash';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/macro';
import cx from 'classnames';
import { PropsWithClassName } from '@/types/common';
import { translatable, TranslatableMessage } from '@/utils/i18n';
import * as styles from './IconFeatures.module.scss';

export enum Feature {
  _1To4People = '1-to-4-people',
  _24Volts = '24-volts',
  _2To4People = '2-to-4-people',
  _30Bleach80Water = '30-bleach-80-water',
  _360 = '360-',
  _40DefreeWash = '40-defree-wash',
  _4To6People = '4-to-6-people',
  _60DegreeWash = '60-degree-wash',
  _6SpeedSelection = '6-speed-selection',
  _6To8People = '6-to-8-people',
  _8To10People = '8-to-10-people',
  AbrasionResistant = 'abrasion-resistant',
  AbsortionSystem = 'absortion-system',
  AcousticInsulation = 'acoustic-insulation',
  Adaptability = 'adaptability',
  AdjustableDesk = 'adjustable-desk',
  AdjustableLeg = 'adjustable-leg',
  AluminiumFrame = 'aluminium-frame',
  AntiMould = 'anti-mould',
  AntiStain = 'anti-stain',
  Ar = 'ar',
  AvSystem = 'av-system',
  BleachOk = 'bleach-ok',
  BreakingTerst300 = 'breaking-terst-300',
  BreakingTest200kg = 'breaking-test-200kg',
  Calendaring = 'calendaring',
  Call117 = 'call-117',
  Ce = 'ce',
  ClassA2 = 'class-a2',
  ClassI = 'class-i',
  ClassIi = 'class-ii',
  ClassIii = 'class-iii',
  ClickAndWorkSystem = 'click-and-work-system',
  CloudSolutions = 'cloud-solutions',
  DryCleaningPce = 'dry-cleaning-pce',
  DoNotWash = 'do-not-wash',
  Durability = 'durability',
  EasyClean = 'easy-clean',
  EasyMaintenance = 'easy-maintenance',
  EcoFriendly = 'eco-friendly',
  EnergyEfficiency = 'energy-efficiency',
  EnvironmentalCertifiedMaterials = 'environmental-certified-materials',
  FastAssembly = 'fast-assembly',
  FireRetardand = 'fire-retardand',
  Folding = 'folding',
  FootSwitchOnOff = 'foot-switch-on-off',
  FullScreenMode = 'full-screen-mode',
  Handwash = 'handwash',
  HasToBeUnderPorch = 'has-to-be-under-porch',
  Indoor = 'indoor',
  IntegratedTechnology = 'integrated-technology',
  InternalMeetings = 'internal-meetings',
  Ip = 'ip',
  Ip65 = 'ip-65',
  IronOk = 'iron-ok',
  Iso140012015 = 'iso140012015',
  Iso90012015 = 'iso90012015',
  IsolatedWork = 'isolated-work',
  Led = 'led',
  LedIncluded = 'led-included',
  LocalSupplier = 'local-supplier',
  LonglifeCycle = 'longlife-cycle',
  LongMeetings = 'long-meetings',
  MachineWashNormal = 'machine-wash-normal',
  MadeInEu = 'made-in-eu',
  MetalFrame = 'metal-frame',
  Modularity = 'modularity',
  MotionActivation = 'motion-activation',
  MovilitySensor = 'movility-sensor',
  MultipleColors = 'multiple-colors',
  MultipleSizes = 'multiple-sizes',
  Nfpa260 = 'nfpa-260',
  NoBleach = 'no-bleach',
  NoDryClean = 'no-dry-clean',
  NoIron = 'no-iron',
  NoThumbleDry = 'no-thumble-dry',
  NotRegularGarbage = 'not-regular-garbage',
  OpenSpaces = 'open-spaces',
  OptionalAccessories = 'optional-accessories',
  OptionalWheels = 'optional-wheels',
  OrientableLight = 'orientable-light',
  OutdoorResistant = 'outdoor-resistant',
  PhysicalComfort = 'physical-comfort',
  PipingOptions = 'piping-options',
  PivotDoor = 'pivot-door',
  PlugNPlay = 'plug-n-play',
  PolymerizationAt160 = 'polymerization-at-160',
  PremiumMaterials = 'premium-materials',
  Rain = 'rain',
  RainSensor = 'rain-sensor',
  RecyclableMaterials = 'recyclable-materials',
  RegularAirflow = 'regular-airflow',
  RemoteControl = 'remote-control',
  RestoreView = 'restore-view',
  ReverseFunctionControl = 'reverse-function-control',
  SafetyGlass = 'safety-glass',
  SecureEndpoint = 'secure-endpoint',
  SingleOrDoubleGazedWallSystem = 'single-or-double-gazed-wall-system',
  SmallSpaces = 'small-spaces',
  Snow = 'snow',
  SnowSensor = 'snow-sensor',
  SoundXxDb = 'sound-xx-db',
  Stackable = 'stackable',
  SuitableIndoor = 'suitable-indoor',
  TemperatureResistant = 'temperature-resistant',
  ThermalWelding = 'thermal-welding',
  Ul = 'ul',
  UnstableTable = 'unstable-table',
  UvResistant = 'uv-resistant',
  VentilationSystem = 'ventilation-system',
  WaterRepellent = 'water-repellent',
  Wind = 'wind',
  WindSensor = 'wind-sensor',
  WithWheel = 'with-wheel',
}

const descriptions: Record<Feature, TranslatableMessage> = {
  [Feature.WithWheel]: translatable(t`With wheel`),
  [Feature.Wind]: translatable(t`Wind`),
  [Feature.WindSensor]: translatable(t`Wind sensor`),
  [Feature.WaterRepellent]: translatable(t`Water repellent`),
  [Feature.VentilationSystem]: translatable(t`Ventilation system`),
  [Feature.UvResistant]: translatable(t`UV resistant`),
  [Feature.UnstableTable]: translatable(t`Unstable table`),
  [Feature.Ul]: translatable(t`UL`),
  [Feature.ThermalWelding]: translatable(t`Thermal welding`),
  [Feature.TemperatureResistant]: translatable(t`Temperature resistant`),
  [Feature.SuitableIndoor]: translatable(t`Suitanable for indoor use only`),
  [Feature.Stackable]: translatable(t`Stackable`),
  [Feature.SoundXxDb]: translatable(t`Sound XX dB`),
  [Feature.Snow]: translatable(t`Snow`),
  [Feature.SnowSensor]: translatable(t`Snow sensor`),
  [Feature.SmallSpaces]: translatable(t`Small spaces`),
  [Feature.SingleOrDoubleGazedWallSystem]: translatable(
    t`Single or double gazed wall system`
  ),
  [Feature.SecureEndpoint]: translatable(t`Secure endpoint`),
  [Feature.SafetyGlass]: translatable(t`Safety glass`),
  [Feature.ReverseFunctionControl]: translatable(t`Reverse function control`),
  [Feature.RestoreView]: translatable(t`Restore view`),
  [Feature.RemoteControl]: translatable(t`Remote control`),
  [Feature.RegularAirflow]: translatable(t`Regular airflow`),
  [Feature.RecyclableMaterials]: translatable(t`Recyclable materials`),
  [Feature.Rain]: translatable(t`Rain`),
  [Feature.RainSensor]: translatable(t`Rain sensor`),
  [Feature.PremiumMaterials]: translatable(t`Premium materials`),
  [Feature.PolymerizationAt160]: translatable(t`Polymerization at 160`),
  [Feature.PlugNPlay]: translatable(t`Plug & play`),
  [Feature.PivotDoor]: translatable(t`Pivot door`),
  [Feature.PipingOptions]: translatable(t`Piping options`),
  [Feature.PhysicalComfort]: translatable(t`Physical comfort`),
  [Feature.OutdoorResistant]: translatable(t`Outdoor resistant`),
  [Feature.OrientableLight]: translatable(t`Orientable light`),
  [Feature.OptionalWheels]: translatable(t`Optional wheels`),
  [Feature.OptionalAccessories]: translatable(t`Optional accessories`),
  [Feature.OpenSpaces]: translatable(t`Open spaces`),
  [Feature.NotRegularGarbage]: translatable(t`Not regular garbage`),
  [Feature.NoThumbleDry]: translatable(t`No thumble dry`),
  [Feature.NoIron]: translatable(t`No iron`),
  [Feature.NoDryClean]: translatable(t`No dry clean`),
  [Feature.NoBleach]: translatable(t`No bleach`),
  [Feature.Nfpa260]: translatable(t`NFPA 260`),
  [Feature.MultipleSizes]: translatable(t`Multiple sizes`),
  [Feature.MultipleColors]: translatable(t`Multiple colors`),
  [Feature.MovilitySensor]: translatable(t`Movility sensor`),
  [Feature.MotionActivation]: translatable(t`Motion activation`),
  [Feature.Modularity]: translatable(t`Modularity`),
  [Feature.MetalFrame]: translatable(t`Metal frame`),
  [Feature.MadeInEu]: translatable(t`Made in EU`),
  [Feature.MachineWashNormal]: translatable(t`Machine wash normal`),
  [Feature.LonglifeCycle]: translatable(t`Long life cycle`),
  [Feature.LongMeetings]: translatable(t`Long meetings`),
  [Feature.LocalSupplier]: translatable(t`Local supplier`),
  [Feature.Led]: translatable(t`LED`),
  [Feature.LedIncluded]: translatable(t`LED included`),
  [Feature.Iso140012015]: translatable(t`ISO 14001:2015`),
  [Feature.Iso90012015]: translatable(t`ISO 9001:2015`),
  [Feature.IsolatedWork]: translatable(t`Isolated work`),
  [Feature.IronOk]: translatable(t`Iron OK`),
  [Feature.Ip]: translatable(t`IP`),
  [Feature.Ip65]: translatable(t`IP 65`),
  [Feature.InternalMeetings]: translatable(t`Internal meetings`),
  [Feature.IntegratedTechnology]: translatable(t`Integrated technology`),
  [Feature.Indoor]: translatable(t`Indoor`),
  [Feature.HasToBeUnderPorch]: translatable(t`Has to be under porch`),
  [Feature.Handwash]: translatable(t`Handwash`),
  [Feature.FullScreenMode]: translatable(t`Full screen mode`),
  [Feature.FootSwitchOnOff]: translatable(t`Foot switch on off`),
  [Feature.Folding]: translatable(t`Folding`),
  [Feature.FireRetardand]: translatable(t`Fire retardand`),
  [Feature.FastAssembly]: translatable(t`Fast assembly`),
  [Feature.EnvironmentalCertifiedMaterials]: translatable(
    t`Environmental certified materials`
  ),
  [Feature.EnergyEfficiency]: translatable(t`Energy efficiency`),
  [Feature.EcoFriendly]: translatable(t`Eco-friendly`),
  [Feature.EasyMaintenance]: translatable(t`Easy maintenance`),
  [Feature.EasyClean]: translatable(t`Easy clean`),

  [Feature.Durability]: translatable(t`Durability`),
  [Feature.DoNotWash]: translatable(t`Do not wash`),
  [Feature.DryCleaningPce]: translatable(t`Dry cleaning PCE`),
  [Feature.CloudSolutions]: translatable(t`Cloud solutions`),
  [Feature.ClickAndWorkSystem]: translatable(t`Click and work system`),
  [Feature.ClassIii]: translatable(t`Class III`),
  [Feature.ClassIi]: translatable(t`Class II`),
  [Feature.ClassI]: translatable(t`Class I`),
  [Feature.ClassA2]: translatable(t`Class A2`),
  [Feature.Ce]: translatable(t`CE`),
  [Feature.Call117]: translatable(t`Call 117`),
  [Feature.Calendaring]: translatable(t`Calendaring`),
  [Feature.BreakingTest200kg]: translatable(t`Breaking test 200kg`),
  [Feature.BreakingTerst300]: translatable(t`Breaking terst 300`),
  [Feature.BleachOk]: translatable(t`Bleach OK`),
  [Feature.AvSystem]: translatable(t`AV system`),
  [Feature.Ar]: translatable(t`AR`),
  [Feature.AntiStain]: translatable(t`Anti stain`),
  [Feature.AntiMould]: translatable(t`Anti mould`),
  [Feature.AluminiumFrame]: translatable(t`Aluminium frame`),
  [Feature.AdjustableLeg]: translatable(t`Adjustable leg`),
  [Feature.AdjustableDesk]: translatable(t`Adjustable desk`),
  [Feature.Adaptability]: translatable(t`Adaptability`),
  [Feature.AcousticInsulation]: translatable(t`Acoustic insulation`),
  [Feature.AbsortionSystem]: translatable(t`Absortion system`),
  [Feature.AbrasionResistant]: translatable(t`Abrasion resistant`),
  [Feature._8To10People]: translatable(t`8 to 10 people`),
  [Feature._6To8People]: translatable(t`6 to 8 people`),
  [Feature._6SpeedSelection]: translatable(t`6 speed selection`),
  [Feature._40DefreeWash]: translatable(t`40 degree wash`),
  [Feature._60DegreeWash]: translatable(t`60 degree wash`),
  [Feature._4To6People]: translatable(t`4 to 6 people`),
  [Feature._360]: translatable(t`360°`),
  [Feature._30Bleach80Water]: translatable(t`30% bleach 80% water`),
  [Feature._24Volts]: translatable(t`24 volts`),
  [Feature._2To4People]: translatable(t`2 to 4 people`),
  [Feature._1To4People]: translatable(t`1 to 4 people`),
};

const safeName = (str: string) => (/^\d/.test(str) ? `x${str}` : str);

const icons = Object.fromEntries(
  map(values(Feature), (feature) => [
    feature,
    require(`@/assets/icons/features/${safeName(feature)}.svg`).default,
  ])
) as Record<Feature, string>;

type Props = PropsWithClassName<{
  features: Feature[];
  isCompact?: boolean;
}>;

const IconFeatures: FC<Props> = ({ className, features, isCompact }) => {
  const { i18n } = useLingui();
  return (
    <ul
      className={cx(
        styles.main,
        {
          [styles.isCompact]: isCompact,
        },
        className
      )}
    >
      {features.map((icon) => (
        <li key={icon} className={styles.feature}>
          <div className={styles.icon}>{React.createElement(icons[icon])}</div>
          <p>{descriptions[icon]?.(i18n)}</p>
        </li>
      ))}
    </ul>
  );
};

export default IconFeatures;
